/**
 * /followups — Follow-ups Admin list (Story 7.14 + 7.14b refactor).
 *
 * Lista paginada com filtros laterais (status multi, campanha UUID, phone
 * substring, range datetime). Row click → /followups/:id. KPI cards no topo
 * mostram aggregate 24h (5 buckets: sent / failed / 3× skipped_*).
 *
 * Refactor 7.14b extracted:
 *   - filter UI    → `FollowupFilters`
 *   - typed client → `lib/followups.ts`
 *   - list query   → `useFollowupsList`
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FollowupFilters } from '@/components/followups/FollowupFilters';
import { FollowupMetricsCards } from '@/components/followups/FollowupMetricsCards';
import { FollowupStatusBadge } from '@/components/followups/FollowupStatusBadge';
import { useFollowupsList } from '@/hooks/useFollowups';
import {
  EMPTY_FOLLOWUP_FILTERS,
  toFollowupQueryString,
  type FollowupListFilters,
} from '@/lib/followups';
import { ApiError } from '@/lib/api';
import { formatDateTime as fmtDateTime } from '@/lib/format-date';

const DEFAULT_PAGE_SIZE = 50;

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatDateTime(iso: string | null): string {
  return fmtDateTime(iso) || '—';
}

export function FollowupsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FollowupListFilters>(EMPTY_FOLLOWUP_FILTERS);
  const [page, setPage] = useState(1);

  const qs = toFollowupQueryString(filters, page, DEFAULT_PAGE_SIZE);
  const listQuery = useFollowupsList(qs);

  const totalPages = listQuery.data
    ? Math.max(1, Math.ceil(listQuery.data.total / listQuery.data.pageSize))
    : 1;

  function handleFiltersChange(next: FollowupListFilters) {
    setFilters(next);
    setPage(1);
  }

  function handleClear() {
    setFilters(EMPTY_FOLLOWUP_FILTERS);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Follow-ups</h1>
        <p className="text-sm text-muted-foreground">
          Histórico de disparos automáticos e reenvios manuais nas últimas 24h.
        </p>
      </div>

      <FollowupMetricsCards />

      <Card>
        <CardContent className="p-4">
          <FollowupFilters value={filters} onChange={handleFiltersChange} onClear={handleClear} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div data-testid="followups-skeleton" className="p-6">
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded bg-muted" />
                ))}
              </div>
            </div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar follow-ups.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Enviado em</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Telefone</th>
                    <th className="px-4 py-2">Campanha</th>
                    <th className="px-4 py-2">Mensagem</th>
                    <th className="px-4 py-2">Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.items.length > 0 ? (
                    listQuery.data.items.map((f) => (
                      <tr
                        key={f.id}
                        data-testid={`followup-row-${f.id}`}
                        className="cursor-pointer border-b hover:bg-accent/40 last:border-b-0"
                        onClick={() => navigate(`/followups/${f.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navigate(`/followups/${f.id}`);
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Abrir follow-up ${f.id}`}
                      >
                        <td className="px-4 py-2">{formatDateTime(f.attemptedAt)}</td>
                        <td className="px-4 py-2">
                          <FollowupStatusBadge status={f.status} />
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{f.maskedPhone}</td>
                        <td className="px-4 py-2">{f.campaignName}</td>
                        <td className="max-w-[12rem] truncate px-4 py-2 text-xs sm:max-w-[20rem]">
                          {f.messageContentSnapshot}
                        </td>
                        <td className="max-w-[16rem] truncate px-4 py-2 text-xs text-destructive">
                          {f.errorMessage ?? '—'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum follow-up encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {listQuery.data && listQuery.data.total > 0 ? (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span className="text-muted-foreground">
            Página {listQuery.data.page} de {totalPages} ({listQuery.data.total} total)
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      ) : null}
    </div>
  );
}
